/**
 * Helper functions for working with save data
 * These provide common patterns for extracting data from the save system
 */

import { LevelScore, Player } from '@mental-mint/shared-types';
import { SaveDataOrchestrator } from './SaveDataOrchestrator';
import { LocalSaveManager } from './LocalSaveManager';
import { isDailyPuzzleGameSaveData } from '@mental-mint/shared-types';

/**
 * Gets a single level score from the game's save data
 * @param game Game object with metadata.gameId
 * @param levelId Level ID to retrieve
 * @returns LevelScore if found, undefined otherwise
 */
export async function getSingleLevelScore(game: { metadata: { gameId: string } }, levelId: string): Promise<LevelScore<any> | undefined> {
  const saveObject = await SaveDataOrchestrator.loadGameData(game.metadata.gameId);
  
  if (!saveObject?.savedGameData) {
    return undefined;
  }
  
  // Use type guard to check for daily puzzle game format
  if (isDailyPuzzleGameSaveData(saveObject.savedGameData)) {
    // Type-safe access to Record<string, LevelScore>
    return saveObject.savedGameData.levelScores[levelId];
  }
  
  // Fallback: try to find level score in any 'levelScores' property
  const levelScores = (saveObject.savedGameData as any).levelScores;
  
  if (!levelScores) {
    return undefined;
  }
  
  // If it's a Record/object, access by key
  if (typeof levelScores === 'object' && !Array.isArray(levelScores)) {
    return levelScores[levelId];
  }
  
  // If it's an Array (legacy format), find by levelId
  if (Array.isArray(levelScores)) {
    return levelScores.find((score: LevelScore<any>) => score.levelId === levelId);
  }
  
  return undefined;
}

/**
 * Gets the current player information
 * Loads from local storage using a simple cookie
 * @returns Player object with id and name
 */
export function getPlayer(): Player {
  const playerData = LocalSaveManager.loadSimpleData<Player>('shared', 'player');
  
  if (playerData?.data) {
    return playerData.data;
  }
  
  // Generate new player if none exists
  const newPlayer: Player = {
    id: `player_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    name: `Player ${Math.floor(Math.random() * 9000) + 1000}`
  };
  
  LocalSaveManager.saveSimpleData('shared', 'player', newPlayer);
  return newPlayer;
}

/**
 * Marks the tutorial as completed for a specific game
 * @param game Game object with metadata.gameId
 */
export async function markTutorialCompleted(game: { metadata: { gameId: string } }): Promise<void> {
  const saveObject = await SaveDataOrchestrator.loadGameData(game.metadata.gameId);
  
  // Start with existing save data, or empty object if no save exists
  const currentData = saveObject?.savedGameData ?? {};
  
  // Update hasCompletedTutorial flag
  const updatedData = {
    ...currentData,
    hasCompletedTutorial: true
  };
  
  await SaveDataOrchestrator.saveGameData(game.metadata.gameId, updatedData);
}

/**
 * Checks if the tutorial has been completed for a specific game
 * @param game Game object with metadata.gameId
 * @returns true if tutorial is completed, false otherwise
 */
export async function hasCompletedTutorial(game: { metadata: { gameId: string } }): Promise<boolean> {
  const saveObject = await SaveDataOrchestrator.loadGameData(game.metadata.gameId);
  return saveObject?.savedGameData?.hasCompletedTutorial === true;
}

/**
 * Merge level scores for upsert operations
 * Handles best time, original time, and num tries tracking
 * @param newScore New score to merge
 * @param oldScore Existing score (if any)
 * @returns Merged score
 */
export function mergeLevelScores(
  newScore: LevelScore<any>, 
  oldScore: LevelScore<any> | undefined
): LevelScore<any> {
  // If there was no previously-saved score, simply return the new score
  if (oldScore == null) {
    return newScore;
  }

  let totalNumTries = undefined;
  // If this game tracks num tries, save it
  if (oldScore.numTries != null || newScore.numTries != null) {
    totalNumTries = (oldScore.numTries ?? 0) + (newScore.numTries ?? 0);
  }

  // Player has already beaten this puzzle
  if (oldScore.isWon) {
    // If this game tracks best time, save it
    if (oldScore.bestTimeSec || newScore.bestTimeSec) {
      oldScore.bestTimeSec = Math.min(oldScore.bestTimeSec ?? Infinity, newScore.bestTimeSec ?? Infinity);
    }
    // If this game tracks original time, only save it if it is not already set
    if (!oldScore.originalTimeSec) {
      oldScore.originalTimeSec = newScore.originalTimeSec;
    }
    // If this game tracks num tries, save it
    oldScore.numTries = totalNumTries;
    return oldScore;
  }

  // Player has not previously beaten this puzzle yet, so aggregate total number of attempts
  newScore.numTries = totalNumTries;
  if (newScore.isWon) newScore.numTriesTillWon = totalNumTries;
  return newScore;
}

/**
 * Update save data with a new level score
 * Returns updated save data (does not mutate input)
 * For daily puzzle games, limits to most recent 21 level scores to prevent unbounded growth
 * @param saveData Current save data
 * @param newScore New level score to upsert
 * @returns Updated save data with merged score
 */
export function upsertLevelScoreInSaveData<T extends { levelScores: Record<string, LevelScore<any>> }>(
  saveData: T,
  newScore: LevelScore<any>
): T {
  const oldScore = saveData.levelScores[newScore.levelId];
  const mergedScore = mergeLevelScores(newScore, oldScore);
  
  let updatedLevelScores = {
    ...saveData.levelScores,
    [newScore.levelId]: mergedScore,
  };
  
  // Limit to 21 most recent level scores for daily puzzle games
  const levelIds = Object.keys(updatedLevelScores);
  if (levelIds.length > 21) {
    // Sort by levelId (which includes date) descending, keep most recent 21
    const sortedIds = levelIds.sort((a, b) => b.localeCompare(a));
    const idsToKeep = sortedIds.slice(0, 21);
    
    updatedLevelScores = Object.fromEntries(
      idsToKeep.map(id => [id, updatedLevelScores[id]])
    );
  }
  
  return {
    ...saveData,
    levelScores: updatedLevelScores,
  };
}
